import { useState, useCallback } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { MediaObject, SuggestionRequestStatus } from 'types'
import { useRefineImage } from 'hooks/queries/designs/useRefineImage'
import { useRefineVideo } from 'hooks/queries/designs/useRefineVideo'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { useProgress } from 'hooks/useProgress'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { RefineSettings } from './components/RefineSettings'
import { Processing } from './components/Processing'
import { RefinedMediaPreview } from './components/RefinedMediaPreview'

interface RefineMediaModalProps {
  mediaObject: MediaObject
  designId: string
}

type ModalState = 'review' | 'processing' | 'result'

export type ImageRefinedContent = {
  media_file_id: string
  image_url: string
  ai_generated: boolean
}

export type VideoRefinedContent = {
  media_file_id: string
  video_url: string
  thumbnail_url: string
}

export type RefinedContent = ImageRefinedContent | VideoRefinedContent

export const RefineMediaModal = ({ mediaObject, designId }: RefineMediaModalProps) => {
  const { closeModal, openModal } = useModals()
  const { usageCosts } = useCreditUsage()
  const isVideo = mediaObject.mime_type?.startsWith('video/')
  const generationCost = isVideo ? usageCosts?.video_credits_cost : usageCosts?.visual_styles_credits_cost

  const initialTopic = mediaObject.provider_data?.topic || ''
  const initialSourceImageUrl = mediaObject.provider_data?.original_image_url || ''
  const [topic, setTopic] = useState(initialTopic)
  const [sourceImageUrl, setSourceImageUrl] = useState(initialSourceImageUrl)
  const [modalState, setModalState] = useState<ModalState>('review')
  const [requestId, setRequestId] = useState<number | null>(null)
  const [refinedContent, setRefinedContent] = useState<RefinedContent | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refineImageMutation = useRefineImage(designId)
  const refineVideoMutation = useRefineVideo(designId)

  const { start: startProgress, progress } = useProgress({
    linearProgress: true,
    immediateComplete: false,
    speed: 100,
  })

  usePollAISuggestionRequest(
    { id: requestId ?? 0 },
    {
      enabled: requestId !== null && modalState === 'processing',
      onSuccess: (data) => {
        if (data.request.status === SuggestionRequestStatus.Successful) {
          try {
            const content = JSON.parse(data.request.generated_content || '{}') as RefinedContent
            setRefinedContent(content)
            setModalState('result')
          } catch {
            setError('Failed to parse refined content')
            setModalState('review')
          }
        } else if (
          data.request.status === SuggestionRequestStatus.Failed ||
          data.request.status === SuggestionRequestStatus.Aborted
        ) {
          setError('Refinement failed. Please try again.')
          setModalState('review')
        }
      },
    },
  )

  const handleRegenerate = useCallback(async () => {
    setError(null)
    setModalState('processing')
    startProgress()

    try {
      const mutation = isVideo ? refineVideoMutation : refineImageMutation

      const response = await mutation.mutateAsync({
        image_url: sourceImageUrl,
        topic,
        ...(isVideo ? {} : { generation_type: mediaObject.provider_data?.generation_type || 'refined' }),
      })

      setRequestId(response.request.id)
    } catch {
      setError('Failed to start refinement. Please try again.')
      setModalState('review')
    }
  }, [
    sourceImageUrl,
    topic,
    isVideo,
    mediaObject.provider_data?.generation_type,
    refineImageMutation,
    refineVideoMutation,
    startProgress,
  ])

  const handleBack = useCallback(() => {
    if (modalState === 'result') {
      setModalState('review')
      setRefinedContent(null)
    } else if (modalState === 'processing') {
      setModalState('review')
      setRequestId(null)
    }
  }, [modalState])

  const handleReplaceImage = useCallback(() => {
    openModal(
      ImageMenuModal,
      {
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
        hiddenProviders: ['stockVideo', 'stockPhoto', 'gifs', 'blazeRecommendations'],
        maxCount: 1,
        title: 'Select Source Image',
        onUse: (images: ImageMenuSelectedImage[]) => {
          if (images.length > 0) {
            setSourceImageUrl(images[0].url)
          }
        },
      },
      {
        newStack: true,
      },
    )
  }, [openModal])

  return (
    <Modal.Root size="lg" onPressOutside={() => {}}>
      {modalState === 'review' && (
        <RefineSettings
          topic={topic}
          setTopic={setTopic}
          sourceImageUrl={sourceImageUrl}
          error={error}
          generationCost={generationCost}
          onClose={closeModal}
          onRegenerate={handleRegenerate}
          onReplaceImage={handleReplaceImage}
        />
      )}
      {modalState === 'processing' && (
        <Processing
          sourceImageUrl={sourceImageUrl}
          progress={progress}
          isVideo={isVideo}
          onClose={closeModal}
          onBack={handleBack}
        />
      )}
      {modalState === 'result' && (
        <RefinedMediaPreview
          isVideo={isVideo}
          refinedContent={refinedContent}
          designId={designId}
          onClose={closeModal}
          onBack={handleBack}
        />
      )}
    </Modal.Root>
  )
}
